import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, formatLKR, formatPct, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import WatchlistStarButton from "@/components/common/WatchlistStarButton";
import HelpTooltip from "@/components/common/HelpTooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowUpRight, AlertTriangle, Info } from "lucide-react";
import { LockedFeature } from "@/components/common/Upgrade";

const BestValuePage = () => {
  const { isPremium } = useAuth();
  const [rows, setRows] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(isPremium);
  const [error, setError] = useState(null);
  const [minGap, setMinGap] = useState([0]);
  const [sector, setSector] = useState("all");
  const [sortKey, setSortKey] = useState("yield_difference_pct");
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    if (!isPremium) return;
    Promise.all([
      apiGet("/comparison/best-value?limit=400"),
      apiGet("/sectors"),
      apiGet("/audit/dual-class/latest").catch(() => null),
    ])
      .then(([r, s, a]) => {
        setRows(r);
        setSectors(s);
        setAudit(a);
      })
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [isPremium]);

  const filtered = useMemo(() => {
    let out = rows.filter((r) => (r.yield_difference_pct ?? 0) >= minGap[0]);
    if (sector !== "all") out = out.filter((r) => r.sector === sector);
    out = out.slice().sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
    return out;
  }, [rows, minGap, sector, sortKey]);

  // Split filtered rows into two semantic groups so the user can tell at a
  // glance which pairs have real yield comparisons vs. price-only comparisons.
  const groupedRows = useMemo(() => {
    const withYield = filtered.filter((r) => r.has_yield_data);
    const withoutYield = filtered.filter((r) => !r.has_yield_data);
    // Sort within each group:
    //   – With-yield: by selected sortKey desc (default = yield_difference_pct)
    //   – Without-yield: always by price_discount_pct desc (most discounted first)
    withYield.sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
    withoutYield.sort(
      (a, b) => Math.abs(b.price_discount_pct ?? 0) - Math.abs(a.price_discount_pct ?? 0)
    );
    return { withYield, withoutYield };
  }, [filtered, sortKey]);

  // Reset filters never affect a separate page state now (grouped view).
  // (Pagination removed in favour of two semantic groups; dataset is still
  // <100 dual-class pairs so all rows fit comfortably in one card.)

  if (!isPremium) {
    return (
      <LockedFeature
        feature="best-value-finder"
        title="Best Value Finder is a Premium feature"
        blurb="Side-by-side comparison of every dual-class CSE stock — discover non-voting shares trading at a discount with significantly higher yields. Unlocked with the free 14-day Premium trial."
        preview={
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="border bg-card">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between"><div className="h-4 w-32 rounded bg-muted" /><div className="h-6 w-20 rounded bg-muted" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-12 rounded bg-muted" />
                    <div className="h-12 rounded bg-muted" />
                  </div>
                  <div className="h-3 w-3/4 rounded bg-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        }
      />
    );
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  return (
    <div className="space-y-6" data-testid="best-value-page">
      <Card className="border bg-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[hsl(var(--warning))]/15 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[hsl(var(--warning))]" />
            </div>
            <div>
              <div className="font-display font-semibold text-lg">Find non-voting opportunities</div>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Non-voting shares often trade at a discount but pay the same dividend per share — producing a higher
                yield. Use the controls below to surface today’s widest yield gaps.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border bg-card">
        <CardHeader>
          <CardTitle className="font-display text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Min yield gap: <span className="text-foreground font-medium">{minGap[0].toFixed(1)}%</span>
            </label>
            <div data-testid="best-value-min-gap-slider" className="mt-3">
              <Slider value={minGap} onValueChange={setMinGap} max={5} step={0.1} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Sector</label>
            <div className="mt-2">
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="best-value-sector-select">
                  <SelectValue placeholder="Sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sectors</SelectItem>
                  {sectors.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Sort by</label>
            <div className="mt-2">
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger data-testid="best-value-sort">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yield_difference_pct">Yield gap %</SelectItem>
                  <SelectItem value="price_discount_pct">Price discount %</SelectItem>
                  <SelectItem value="non_voting_yield">Non-voting yield</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="best-value-table">
            <thead className="bg-card sticky top-0">
              <tr className="border-b">
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Company</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Sector</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span className="inline-flex items-center justify-end">Voting<HelpTooltip termId="voting-vs-nonvoting" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span className="inline-flex items-center justify-end">Non-voting<HelpTooltip termId="non-voting-shares" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Discount</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span className="inline-flex items-center justify-end">Voting yield<HelpTooltip termId="dividend-yield" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                  <span className="inline-flex items-center justify-end">NV yield<HelpTooltip termId="dividend-yield" size="xs" /></span>
                </th>
                <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Yield gap</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    No matches — try lowering the minimum yield gap.
                  </td>
                </tr>
              )}

              {/* ---------- GROUP 1: With dividend yield data ---------- */}
              {groupedRows.withYield.length > 0 && (
                <tr className="bg-muted/40 border-b" data-testid="best-value-group-with-yield">
                  <td colSpan={9} className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 text-[10px]">
                        With dividend yield data
                      </Badge>
                      <span className="text-xs font-semibold text-foreground">
                        {groupedRows.withYield.length} pair{groupedRows.withYield.length === 1 ? "" : "s"}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        — sorted by yield gap (largest first)
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {groupedRows.withYield.map((r) => (
                <BestValueRow key={`y-${r.company_code}`} r={r} />
              ))}

              {/* ---------- GROUP 2: Price discount only ---------- */}
              {groupedRows.withoutYield.length > 0 && (
                <tr className="bg-muted/40 border-b border-t" data-testid="best-value-group-no-yield">
                  <td colSpan={9} className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-muted text-muted-foreground text-[10px]">
                        Price discount only — no dividend data yet
                      </Badge>
                      <span className="text-xs font-semibold text-foreground">
                        {groupedRows.withoutYield.length} pair{groupedRows.withoutYield.length === 1 ? "" : "s"}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        — sorted by price discount
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {groupedRows.withoutYield.map((r) => (
                <BestValueRow key={`n-${r.company_code}`} r={r} />
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30 text-xs">
            <div className="text-muted-foreground" data-testid="best-value-count">
              <span className="font-medium text-foreground">{filtered.length}</span> verified dual-class pair{filtered.length === 1 ? "" : "s"}
              {" "}· <span className="text-[hsl(var(--success))]">{groupedRows.withYield.length}</span> with yield
              {" "}· <span className="text-muted-foreground">{groupedRows.withoutYield.length}</span> price-only
              {audit?.completed_at && (
                <>
                  {" "}·{" "}
                  <span className="text-foreground/80" data-testid="best-value-last-audited">
                    Last audited: {formatDate(audit.completed_at)}
                  </span>
                  {audit.fake_removed > 0 && (
                    <span className="ml-1 text-[10px]">
                      ({audit.fake_removed} fake removed)
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5 px-1" data-testid="best-value-footer-note">
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        Yield gap is calculated using the most recent dividend per share and current market price. Stocks
        with no yield data have not yet declared dividends in our records — they are grouped at the bottom
        and ranked by price discount instead.
      </p>
    </div>
  );
};

const BestValueRow = ({ r }) => (
  <tr className="border-b last:border-0 hover:bg-muted/40 transition-colors duration-150">
    <td className="px-4 py-3">
      <div className="flex items-start gap-2">
        <WatchlistStarButton
          cseSymbol={r.non_voting_symbol}
          companyName={r.company_name}
        />
        <div className="min-w-0">
          <CompanyLink
            companyCode={r.company_code}
            name={r.company_name}
            fromLabel="Best Value Finder"
            className="font-medium block"
            testId={`bv-name-${r.company_code}`}
          />
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-1.5">
            <SymbolLink companyCode={r.company_code} symbol={r.voting_symbol} fromLabel="Best Value Finder" />
            <span>·</span>
            <SymbolLink companyCode={r.company_code} symbol={r.non_voting_symbol} fromLabel="Best Value Finder" />
          </div>
        </div>
      </div>
    </td>
    <td className="px-4 py-3 text-muted-foreground">{r.sector}</td>
    <td className="px-4 py-3 text-right tabular">
      {r.data_warning ? (
        <span className="inline-flex items-center gap-1 text-[hsl(var(--warning))]" title={r.data_warning}>
          <AlertTriangle className="h-3 w-3" />
          {formatLKR(r.voting_price)}
        </span>
      ) : formatLKR(r.voting_price)}
    </td>
    <td className="px-4 py-3 text-right tabular">
      {r.data_warning ? (
        <span className="inline-flex items-center gap-1 text-[hsl(var(--warning))]" title={r.data_warning}>
          <AlertTriangle className="h-3 w-3" />
          {formatLKR(r.non_voting_price)}
        </span>
      ) : formatLKR(r.non_voting_price)}
    </td>
    <td className="px-4 py-3 text-right tabular">
      {r.data_warning ? (
        <span className="text-[hsl(var(--warning))] text-xs italic">Price data may be incorrect</span>
      ) : (
        <span className="text-[hsl(var(--info))]">{formatPct(-Math.abs(r.price_discount_pct))}</span>
      )}
    </td>
    <td className="px-4 py-3 text-right tabular">
      {r.has_yield_data ? formatPct(r.voting_yield) : <span className="text-muted-foreground">—</span>}
    </td>
    <td className="px-4 py-3 text-right tabular">
      {r.has_yield_data
        ? <span className="text-[hsl(var(--success))]">{formatPct(r.non_voting_yield)}</span>
        : <span className="text-muted-foreground italic text-xs">No dividend data</span>}
    </td>
    <td className="px-4 py-3 text-right tabular font-semibold">
      {r.has_yield_data
        ? <span className="text-[hsl(var(--warning))]">{formatPct(r.yield_difference_pct)}</span>
        : <span className="text-muted-foreground">—</span>}
    </td>
    <td className="px-4 py-3">
      <Link
        to={`/stocks/${r.company_code}`}
        state={{ from: { pathname: "/best-value", label: "Best Value Finder" } }}
        className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
      >
        View <ArrowUpRight className="h-3 w-3" />
      </Link>
    </td>
  </tr>
);

export default BestValuePage;
